/**
 * Animation Presets
 * Pre-built animation effects that can be applied to layers
 */

import type { AnimationTrack, Keyframe, AnimationPreset, Easing } from '@/types/animation';
import { createTrack, createKeyframe } from '@/types/animation';

// ============================================================================
// PRESET DEFINITIONS
// ============================================================================

interface PresetConfig {
  keyframes: Keyframe[];
  duration: number;
  easing: Easing;
  fillMode?: AnimationTrack['fillMode'];
}

const presetConfigs: Record<AnimationPreset, PresetConfig> = {
  // ========== FADE ENTRANCES ==========
  fadeIn: {
    duration: 300,
    easing: 'ease-out',
    keyframes: [
      createKeyframe(0, { opacity: 0 }),
      createKeyframe(300, { opacity: 1 }),
    ],
    fillMode: 'forwards',
  },

  fadeInUp: {
    duration: 400,
    easing: 'ease-out-cubic',
    keyframes: [
      createKeyframe(0, { opacity: 0, y: 20 }),
      createKeyframe(400, { opacity: 1, y: 0 }),
    ],
    fillMode: 'forwards',
  },

  fadeInDown: {
    duration: 400,
    easing: 'ease-out-cubic',
    keyframes: [
      createKeyframe(0, { opacity: 0, y: -20 }),
      createKeyframe(400, { opacity: 1, y: 0 }),
    ],
    fillMode: 'forwards',
  },

  fadeInLeft: {
    duration: 400,
    easing: 'ease-out-cubic',
    keyframes: [
      createKeyframe(0, { opacity: 0, x: -20 }),
      createKeyframe(400, { opacity: 1, x: 0 }),
    ],
    fillMode: 'forwards',
  },

  fadeInRight: {
    duration: 400,
    easing: 'ease-out-cubic',
    keyframes: [
      createKeyframe(0, { opacity: 0, x: 20 }),
      createKeyframe(400, { opacity: 1, x: 0 }),
    ],
    fillMode: 'forwards',
  },

  // ========== SCALE ENTRANCES ==========
  scaleIn: {
    duration: 400,
    easing: 'ease-out-back',
    keyframes: [
      createKeyframe(0, { opacity: 0, scale: 0.8 }),
      createKeyframe(400, { opacity: 1, scale: 1 }),
    ],
    fillMode: 'forwards',
  },

  zoomIn: {
    duration: 400,
    easing: 'ease-out-cubic',
    keyframes: [
      createKeyframe(0, { opacity: 0, scale: 0.3 }),
      createKeyframe(400, { opacity: 1, scale: 1 }),
    ],
    fillMode: 'forwards',
  },

  // ========== SLIDE ENTRANCES ==========
  slideInUp: {
    duration: 400,
    easing: 'ease-out-cubic',
    keyframes: [
      createKeyframe(0, { y: 100 }),
      createKeyframe(400, { y: 0 }),
    ],
    fillMode: 'forwards',
  },

  slideInDown: {
    duration: 400,
    easing: 'ease-out-cubic',
    keyframes: [
      createKeyframe(0, { y: -100 }),
      createKeyframe(400, { y: 0 }),
    ],
    fillMode: 'forwards',
  },

  slideInLeft: {
    duration: 400,
    easing: 'ease-out-cubic',
    keyframes: [
      createKeyframe(0, { x: -100 }),
      createKeyframe(400, { x: 0 }),
    ],
    fillMode: 'forwards',
  },

  slideInRight: {
    duration: 400,
    easing: 'ease-out-cubic',
    keyframes: [
      createKeyframe(0, { x: 100 }),
      createKeyframe(400, { x: 0 }),
    ],
    fillMode: 'forwards',
  },

  // ========== BOUNCE ENTRANCE ==========
  bounceIn: {
    duration: 600,
    easing: 'ease-out',
    keyframes: [
      createKeyframe(0, { opacity: 0, scale: 0.3 }),
      createKeyframe(200, { scale: 1.1 }, 'ease-out'),
      createKeyframe(400, { scale: 0.9 }, 'ease-in-out'),
      createKeyframe(600, { opacity: 1, scale: 1 }, 'ease-out'),
    ],
    fillMode: 'forwards',
  },

  // ========== FLIP ENTRANCES ==========
  flipInX: {
    duration: 500,
    easing: 'ease-out',
    keyframes: [
      createKeyframe(0, { opacity: 0, rotation: -90 }),
      createKeyframe(500, { opacity: 1, rotation: 0 }),
    ],
    fillMode: 'forwards',
  },

  flipInY: {
    duration: 500,
    easing: 'ease-out',
    keyframes: [
      createKeyframe(0, { opacity: 0, rotation: 90 }),
      createKeyframe(500, { opacity: 1, rotation: 0 }),
    ],
    fillMode: 'forwards',
  },

  // ========== FADE EXITS ==========
  fadeOut: {
    duration: 300,
    easing: 'ease-in',
    keyframes: [
      createKeyframe(0, { opacity: 1 }),
      createKeyframe(300, { opacity: 0 }),
    ],
    fillMode: 'forwards',
  },

  fadeOutUp: {
    duration: 400,
    easing: 'ease-in-cubic',
    keyframes: [
      createKeyframe(0, { opacity: 1, y: 0 }),
      createKeyframe(400, { opacity: 0, y: -20 }),
    ],
    fillMode: 'forwards',
  },

  fadeOutDown: {
    duration: 400,
    easing: 'ease-in-cubic',
    keyframes: [
      createKeyframe(0, { opacity: 1, y: 0 }),
      createKeyframe(400, { opacity: 0, y: 20 }),
    ],
    fillMode: 'forwards',
  },

  fadeOutLeft: {
    duration: 400,
    easing: 'ease-in-cubic',
    keyframes: [
      createKeyframe(0, { opacity: 1, x: 0 }),
      createKeyframe(400, { opacity: 0, x: -20 }),
    ],
    fillMode: 'forwards',
  },

  fadeOutRight: {
    duration: 400,
    easing: 'ease-in-cubic',
    keyframes: [
      createKeyframe(0, { opacity: 1, x: 0 }),
      createKeyframe(400, { opacity: 0, x: 20 }),
    ],
    fillMode: 'forwards',
  },

  // ========== SCALE EXITS ==========
  scaleOut: {
    duration: 400,
    easing: 'ease-in-back',
    keyframes: [
      createKeyframe(0, { opacity: 1, scale: 1 }),
      createKeyframe(400, { opacity: 0, scale: 0.8 }),
    ],
    fillMode: 'forwards',
  },

  zoomOut: {
    duration: 400,
    easing: 'ease-in-cubic',
    keyframes: [
      createKeyframe(0, { opacity: 1, scale: 1 }),
      createKeyframe(400, { opacity: 0, scale: 0.3 }),
    ],
    fillMode: 'forwards',
  },

  // ========== SLIDE EXITS ==========
  slideOutUp: {
    duration: 400,
    easing: 'ease-in-cubic',
    keyframes: [
      createKeyframe(0, { y: 0 }),
      createKeyframe(400, { y: -100 }),
    ],
    fillMode: 'forwards',
  },

  slideOutDown: {
    duration: 400,
    easing: 'ease-in-cubic',
    keyframes: [
      createKeyframe(0, { y: 0 }),
      createKeyframe(400, { y: 100 }),
    ],
    fillMode: 'forwards',
  },

  slideOutLeft: {
    duration: 400,
    easing: 'ease-in-cubic',
    keyframes: [
      createKeyframe(0, { x: 0 }),
      createKeyframe(400, { x: -100 }),
    ],
    fillMode: 'forwards',
  },

  slideOutRight: {
    duration: 400,
    easing: 'ease-in-cubic',
    keyframes: [
      createKeyframe(0, { x: 0 }),
      createKeyframe(400, { x: 100 }),
    ],
    fillMode: 'forwards',
  },

  bounceOut: {
    duration: 600,
    easing: 'ease-in',
    keyframes: [
      createKeyframe(0, { opacity: 1, scale: 1 }),
      createKeyframe(200, { scale: 0.9 }, 'ease-out'),
      createKeyframe(400, { scale: 1.1 }, 'ease-in-out'),
      createKeyframe(600, { opacity: 0, scale: 0.3 }, 'ease-in'),
    ],
    fillMode: 'forwards',
  },

  // ========== ATTENTION ==========
  pulse: {
    duration: 600,
    easing: 'ease-in-out',
    keyframes: [
      createKeyframe(0, { scale: 1 }),
      createKeyframe(300, { scale: 1.05 }),
      createKeyframe(600, { scale: 1 }),
    ],
    fillMode: 'none',
  },

  bounce: {
    duration: 800,
    easing: 'ease-out',
    keyframes: [
      createKeyframe(0, { y: 0 }),
      createKeyframe(200, { y: -30 }, 'ease-out'),
      createKeyframe(400, { y: 0 }, 'ease-in'),
      createKeyframe(500, { y: -15 }, 'ease-out'),
      createKeyframe(650, { y: 0 }, 'ease-in'),
      createKeyframe(750, { y: -4 }, 'ease-out'),
      createKeyframe(800, { y: 0 }, 'ease-in'),
    ],
    fillMode: 'none',
  },

  shake: {
    duration: 500,
    easing: 'ease-in-out',
    keyframes: [
      createKeyframe(0, { x: 0 }),
      createKeyframe(100, { x: -10 }),
      createKeyframe(200, { x: 10 }),
      createKeyframe(300, { x: -10 }),
      createKeyframe(400, { x: 10 }),
      createKeyframe(500, { x: 0 }),
    ],
    fillMode: 'none',
  },

  wobble: {
    duration: 800,
    easing: 'ease-in-out',
    keyframes: [
      createKeyframe(0, { x: 0, rotation: 0 }),
      createKeyframe(120, { x: -25, rotation: -5 }),
      createKeyframe(280, { x: 20, rotation: 3 }),
      createKeyframe(440, { x: -15, rotation: -3 }),
      createKeyframe(600, { x: 10, rotation: 2 }),
      createKeyframe(760, { x: -5, rotation: -1 }),
      createKeyframe(800, { x: 0, rotation: 0 }),
    ],
    fillMode: 'none',
  },

  flash: {
    duration: 500,
    easing: 'ease-in-out',
    keyframes: [
      createKeyframe(0, { opacity: 1 }),
      createKeyframe(125, { opacity: 0 }),
      createKeyframe(250, { opacity: 1 }),
      createKeyframe(375, { opacity: 0 }),
      createKeyframe(500, { opacity: 1 }),
    ],
    fillMode: 'none',
  },

  heartbeat: {
    duration: 1000,
    easing: 'ease-in-out',
    keyframes: [
      createKeyframe(0, { scale: 1 }),
      createKeyframe(140, { scale: 1.3 }),
      createKeyframe(280, { scale: 1 }),
      createKeyframe(420, { scale: 1.3 }),
      createKeyframe(700, { scale: 1 }),
    ],
    fillMode: 'none',
  },

  // ========== CONTINUOUS ==========
  spin: {
    duration: 1000,
    easing: 'linear',
    keyframes: [
      createKeyframe(0, { rotation: 0 }),
      createKeyframe(1000, { rotation: 360 }),
    ],
    fillMode: 'none',
  },

  float: {
    duration: 3000,
    easing: 'ease-in-out',
    keyframes: [
      createKeyframe(0, { y: 0 }),
      createKeyframe(1500, { y: -10 }),
      createKeyframe(3000, { y: 0 }),
    ],
    fillMode: 'none',
  },

  breathe: {
    duration: 4000,
    easing: 'ease-in-out',
    keyframes: [
      createKeyframe(0, { scale: 1, opacity: 1 }),
      createKeyframe(2000, { scale: 1.05, opacity: 0.8 }),
      createKeyframe(4000, { scale: 1, opacity: 1 }),
    ],
    fillMode: 'none',
  },
};

// ============================================================================
// PRESET API
// ============================================================================

/**
 * Get all available presets
 */
export function getPresets(): AnimationPreset[] {
  return Object.keys(presetConfigs) as AnimationPreset[];
}

/**
 * Get presets by category
 */
export function getPresetsByCategory(): Record<string, AnimationPreset[]> {
  return {
    'Fade In': ['fadeIn', 'fadeInUp', 'fadeInDown', 'fadeInLeft', 'fadeInRight'],
    'Scale In': ['scaleIn', 'zoomIn', 'bounceIn'],
    'Slide In': ['slideInUp', 'slideInDown', 'slideInLeft', 'slideInRight'],
    'Flip In': ['flipInX', 'flipInY'],
    'Fade Out': ['fadeOut', 'fadeOutUp', 'fadeOutDown', 'fadeOutLeft', 'fadeOutRight'],
    'Scale Out': ['scaleOut', 'zoomOut', 'bounceOut'],
    'Slide Out': ['slideOutUp', 'slideOutDown', 'slideOutLeft', 'slideOutRight'],
    'Attention': ['pulse', 'bounce', 'shake', 'wobble', 'flash', 'heartbeat'],
    'Continuous': ['spin', 'float', 'breathe'],
  };
}

/**
 * Create an animation track from a preset
 */
export function createTrackFromPreset(
  preset: AnimationPreset,
  layerId: string,
  options?: {
    name?: string;
    startTime?: number;
    duration?: number;
    repeat?: AnimationTrack['repeat'];
  }
): AnimationTrack {
  const config = presetConfigs[preset];

  // Scale keyframes if duration is customized
  let keyframes = config.keyframes;
  if (options?.duration && options.duration !== config.duration) {
    const scale = options.duration / config.duration;
    keyframes = keyframes.map(kf => ({
      ...kf,
      time: kf.time * scale,
    }));
  }

  return createTrack(
    layerId,
    options?.name ?? preset,
    keyframes,
    {
      startTime: options?.startTime ?? 0,
      duration: options?.duration ?? config.duration,
      easing: config.easing,
      fillMode: config.fillMode,
      repeat: options?.repeat,
    }
  );
}

/**
 * Get preset configuration
 */
export function getPresetConfig(preset: AnimationPreset): PresetConfig {
  return presetConfigs[preset];
}

/**
 * Check if a preset is continuous (should loop)
 */
export function isContinuousPreset(preset: AnimationPreset): boolean {
  return ['spin', 'float', 'breathe'].includes(preset);
}

/**
 * Check if a preset is an entrance animation
 */
export function isEntrancePreset(preset: AnimationPreset): boolean {
  return preset.startsWith('fadeIn') ||
    preset.startsWith('slideIn') ||
    preset.startsWith('scaleIn') ||
    preset.startsWith('zoomIn') ||
    preset.startsWith('bounceIn') ||
    preset.startsWith('flipIn');
}

/**
 * Check if a preset is an exit animation
 */
export function isExitPreset(preset: AnimationPreset): boolean {
  return preset.startsWith('fadeOut') ||
    preset.startsWith('slideOut') ||
    preset.startsWith('scaleOut') ||
    preset.startsWith('zoomOut') ||
    preset.startsWith('bounceOut');
}
